import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

// Register the service worker for background notifications
async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      return reg;
    } catch (err) {
      // Service worker registration is best-effort; failure is non-fatal
      return null;
    }
  }
  return null;
}

function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

// Send notification through Service Worker (works when minimized)
function showBrowserNotification(title, body, data) {
  if ('Notification' in window && Notification.permission === 'granted') {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      // Use Service Worker for reliable background delivery
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title,
        body,
        tag: `fintrack-${Date.now()}`,
        data: data || {},
      });
    } else {
      // Fallback to direct Notification if SW not ready
      try {
        new Notification(title, { body, icon: '/favicon.ico', tag: `fintrack-${Date.now()}` });
      } catch { /* silent */ }
    }
  }
}

export function useWebSocket() {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const showDigestToast = useCallback((data) => {
    const stats = data.stats || {};
    toast(
      <div data-testid="daily-digest-toast">
        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px', color: '#1e1b4b' }}>
          Daily Digest
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: '13px' }}>
          <span style={{ color: '#64748b' }}>Open tasks:</span>
          <span style={{ fontWeight: 600 }}>{stats.total ?? '-'}</span>
          <span style={{ color: '#64748b' }}>Due today:</span>
          <span style={{ fontWeight: 600, color: '#d97706' }}>{stats.due_today ?? '-'}</span>
          <span style={{ color: '#64748b' }}>Overdue:</span>
          <span style={{ fontWeight: 600, color: '#dc2626' }}>{stats.overdue ?? '-'}</span>
          <span style={{ color: '#64748b' }}>In progress:</span>
          <span style={{ fontWeight: 600, color: '#5B6FD6' }}>{stats.in_progress ?? '-'}</span>
          <span style={{ color: '#64748b' }}>With client:</span>
          <span style={{ fontWeight: 600 }}>{stats.pending_client ?? '-'}</span>
        </div>
        {data.overdue_tasks?.length > 0 && (
          <div style={{ marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626', marginBottom: '2px' }}>Top Overdue:</div>
            {data.overdue_tasks.slice(0, 3).map((t) => (
              <div key={t} style={{ fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>{t}</div>
            ))}
          </div>
        )}
      </div>,
      { duration: 15000 }
    );
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');
    if (!user.id || !token) return;

    // Register service worker + request notification permission
    registerServiceWorker();
    requestNotificationPermission();

    const connect = () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace(/^https?/, m => m === 'https' ? 'wss' : 'ws') + `/api/ws/notifications/${user.id}?token=${localStorage.getItem('token')}`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          const ping = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, 15000);
          ws._pingInterval = ping;
        };

        ws.onmessage = (event) => {
          if (event.data === 'pong' || event.data === 'ping') return;
          try {
            const data = JSON.parse(event.data);

            // In-app toast notifications (visible when tab is active)
            if (data.type === 'task_assigned') {
              toast.info(data.message, { duration: 6000, description: `Assigned by ${data.by}` });
            } else if (data.type === 'task_overdue') {
              toast.error(data.message, { duration: 8000 });
            } else if (data.type === 'daily_digest') {
              showDigestToast(data);
            } else if (data.type === 'task_reminder') {
              toast.warning(data.message, { duration: 10000, description: 'Task Reminder' });
            } else if (data.type === 'monthly_report') {
              toast.success(data.message, { duration: 12000, description: 'Monthly Report Ready' });
            } else if (data.type === 'task_transfer_request') {
              toast.info(data.message, { duration: 8000, description: 'Transfer Request' });
            } else if (data.type === 'transfer_accepted') {
              toast.success(data.message, { duration: 6000, description: 'Transfer Accepted' });
            } else if (data.type === 'transfer_rejected') {
              toast.warning(data.message, { duration: 6000, description: 'Transfer Rejected' });
            } else if (data.type === 'review_request') {
              toast.info(data.message, { duration: 8000, description: 'Review Request' });
            } else if (data.type === 'leave_transfer_request') {
              toast.info(data.message, { duration: 8000, description: 'Leave Transfer' });
            } else if (data.type === 'login_alert') {
              toast.warning(data.message, { duration: 15000, description: 'Login Alert' });
            } else if (data.type === 'status_update' || data.type === 'task_update') {
              toast.info(data.message, { duration: 6000, description: data.by ? `By ${data.by}` : undefined });
            } else if (data.message) {
              toast.info(data.message, { duration: 6000 });
            }

            // OS-level push notification via Service Worker (works when minimized)
            if (data.message && data.type !== 'daily_digest') {
              const title = data.type === 'task_assigned' ? 'New Task Assigned'
                : data.type === 'task_overdue' ? 'Task Overdue'
                : data.type === 'task_transfer_request' ? 'Transfer Request'
                : data.type === 'transfer_accepted' ? 'Transfer Accepted'
                : data.type === 'transfer_rejected' ? 'Transfer Rejected'
                : data.type === 'review_request' ? 'Review Request'
                : data.type === 'leave_transfer_request' ? 'Leave Transfer Request'
                : data.type === 'login_alert' ? 'Login Alert'
                : data.type === 'task_reminder' ? 'Task Reminder'
                : data.type === 'status_update' || data.type === 'task_update' ? 'Task Update'
                : 'FinTrack Pro';
              showBrowserNotification(title, data.message, { taskId: data.task_id, type: data.type });
            }

            // Dispatch custom event so pages auto-refresh their data
            window.dispatchEvent(new CustomEvent('ws-notification', { detail: data }));

          } catch { /* ignore non-JSON */ }
        };

        ws.onclose = () => {
          if (ws._pingInterval) clearInterval(ws._pingInterval);
          reconnectTimer.current = setTimeout(connect, 5000);
        };

        ws.onerror = () => { ws.close(); };
      } catch { /* silent */ }
    };

    connect();

    // Network resilience: when browser regains connectivity OR tab is refocused,
    // force-reconnect the WebSocket and broadcast a refresh event so every page
    // re-fetches its data (active timers, tasks, notifications) from the server.
    const forceResync = (reason) => {
      // Close existing socket to trigger reconnect cycle immediately
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        try { wsRef.current.close(); } catch (e) { /* ignore */ }
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(connect, 500);
      // Tell every page to re-fetch its data from server (timers will resume from DB state)
      window.dispatchEvent(new CustomEvent('ws-notification', { detail: { type: 'resync', reason } }));
      if (reason === 'online') {
        toast.success('Back online — syncing latest data...', { duration: 3000 });
      }
    };
    const handleOnline = () => forceResync('online');
    const handleOffline = () => {
      toast.warning('You are offline. Your work is saved locally and will sync when you reconnect.', { duration: 5000 });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') forceResync('focus');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [showDigestToast]);
}
